import { useAppContext } from "@agtech/core/app/AgTechAppContext";
import { AgTechDataAction, AgTechCoreDataActionProps, AgTechDataActionConfiguration, AgTechDataActionContext, DataActionContext, useDataActionExecutor, DefaultAgTechDataActionConfiguration } from "@agtech/core/data/actions/AgTechDataActions";
import { AgTechConfirmationProps } from "@agtech/core/functions/AgTechAppConfirmations";
import AgTechColors from "@agtech/core/styles/AgTechColors";
import { agtechDelay } from "@agtech/core/utilities/AgTechUtilities";
import { AgTechClickEventHandler, AgTechButton } from "../../Buttons/AgTechButtons";
import AgTechTitleDetailHeader from "../../Headers/AgTechTitleDetailHeader";
import AgTechIconBadge from "../../Icons/AgTechIconBadge";
import AgTechFadeInView from "../../Pages/Loading/AgTechFadeInView";
import AgTechText from "../../Text/AgTechText";
import { useState, useEffect, useRef } from "react";
import AgTechHeaderDetail from "../../Headers/AgTechHeaderDetail";
import { waitOnQuery } from "@agtech/core/data/queries/AgTechDataQueries";
import AgTechLoadingSurface from "../../Pages/Loading/AgTechLoadingSurface";
import { logDev } from "@agtech/core/logging/AgTechLogger";
import AgTechModal, { AgTechModalRenderProps } from "./AgTechModal";
import AgTechFixedContainer from "../../Layout/AgTechFixedContainer";

enum AgTechActionModalStatus {
    Open,
    Submitting,
    Submitted
}

export type AgTechActionModalState = {
    status: AgTechActionModalStatus,
    confirmationProps?: AgTechConfirmationProps
}

export type AgTechActionModalProps<TInput, TOutput, TContextData = TInput> = {
    action: AgTechDataAction<TInput, TOutput, TContextData>,
    actionProps: AgTechCoreDataActionProps<TInput>,
    modalProps?: AgTechModalRenderProps
}

export const AgTechActionModal = <TInput, TOutput, TContextData>(
    props: AgTechActionModalProps<TInput, TOutput, TContextData> & {
        closeModal: () => void
    }
) => {
    const [modalStatus, setModalStatus] = useState(AgTechActionModalStatus.Open);
    const [confirmationProps, setConfirmationProps] = useState<AgTechConfirmationProps | undefined>(undefined);

    const appContext = useAppContext();

    const actionConfiguration: AgTechDataActionConfiguration = props.action.getConfiguration 
        ? props.action.getConfiguration(props.actionProps)
        : DefaultAgTechDataActionConfiguration;

    useEffect(() => {
        if (props.action.confirm) {
            props.action.confirm!({
                appContext: {
                    ...appContext,
                    confirmations: {
                        askForConfirmation: async data => {
                            setConfirmationProps(data);
                            return true;
                        }
                    }
                },
                submittedEntity: props.actionProps.submittedEntity
            });
        }
    }, []);

    const modalActionContext: AgTechDataActionContext<any, any> = {
        actionName: props.action.name,
        onExecutionStarted: async () => {
            setModalStatus(AgTechActionModalStatus.Submitting);
            await agtechDelay(600);
        },
        onExecutionSuccessful: async () => {
            setModalStatus(AgTechActionModalStatus.Submitted);

            await agtechDelay(actionConfiguration.actionConfirmationDelay ?? 200);

            props.closeModal();
        },
        onExecutionCompleted: async () => {

        },
        onExecutionFailed: async props => {
            setModalStatus(AgTechActionModalStatus.Open);
        }
    }

    return (
        <AgTechModal
            closeModal={props.closeModal}
            showCloseButton={props.modalProps?.showCloseButton ?? false}
            minWidth='360px'
            maxWidth='480px'
        >
            <AgTechLoadingSurface
                waitOn={confirmationProps !== undefined}
                showLoader={false}
            >
                <AgTechFixedContainer classes="row-based">
                    <DataActionContext.Provider value={modalActionContext}>
                        { modalStatus === AgTechActionModalStatus.Submitting ? (
                            <AgTechActionModalSubmittingView actionConfiguration={actionConfiguration} />
                        ) : modalStatus === AgTechActionModalStatus.Submitted ? (
                            <AgTechActionModalConfirmationView actionConfiguration={actionConfiguration} />
                        ) : (
                            <AgTechActionModalBody
                                action={props.action}
                                actionProps={props.actionProps}
                                confirmationProps={confirmationProps!}
                                closeModal={props.closeModal}
                            />
                        )}
                    </DataActionContext.Provider>
                </AgTechFixedContainer>
            </AgTechLoadingSurface>
        </AgTechModal>
    )
}

const AgTechActionModalBody = <TInput, TOutput, TContextData>(props: {
    action: AgTechDataAction<TInput, TOutput, TContextData>,
    actionProps: AgTechCoreDataActionProps<TInput>,
    confirmationProps: AgTechConfirmationProps,
    closeModal: () => void,
}) => {
    const dataActionExecutor = useDataActionExecutor();
    const confirmButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (confirmButtonRef.current) {
            confirmButtonRef.current.focus();
        }
    }, []);

    const handleCancel = (e: AgTechClickEventHandler) => {
        e.clickEvent.stopPropagation();
        props.closeModal();
    }

    const confirmationProps = props.confirmationProps;

    return (
        <>
            <div className="tall row-based justify-center flexed p-3 container">
                <AgTechHeaderDetail
                    header={confirmationProps.content.header}
                    detail={confirmationProps.content.details}
                    settings={{
                        header: {
                            classes: '!text-plus'
                        },
                        detail: {
                            classes: '!mt-3 !text-base'
                        }
                    }}
                />
            </div>
            <div className="wide column-based items-center p-3 mt-1 border-t container">
                <AgTechButton
                    classes="ml-auto mr-2"
                    text={confirmationProps.content.cancelButtonText ?? "Cancel"}
                    icon="fas fa-times"
                    action={handleCancel}
                />
                <AgTechButton
                    text={confirmationProps.content.confirmationButtonText ?? "Continue"}
                    icon="fas fa-check"
                    classes='button-highlight'
                    ref={confirmButtonRef}
                    action={e => {
                        e.clickEvent.stopPropagation();
                        
                        dataActionExecutor.executeAction(props.action, {
                            ...props.actionProps,
                            settings: {
                                shouldConfirmExecution: false
                            }
                        });
                    }}
                />
            </div>
        </>
    )
}

const AgTechActionModalSubmittingView = (props: {
    actionConfiguration: AgTechDataActionConfiguration
}) => {
    return (
        <div className="stretched absolute bg-white container">
            <div className="stretched row-based centered container">
                <div className="loader" />
                <h1 className="text-plus font-semibold mt-4">{props.actionConfiguration.actionExecutionMessage ?? ''}</h1>      
            </div>
        </div>
    )
}

export const AgTechActionModalConfirmationView = <TFormInput, TFormOutput>(props: {
    actionConfiguration: AgTechDataActionConfiguration
}) => {
    return (
        <div className="stretched absolute bg-white centered container">
            <AgTechFadeInView classes='centered'>
                <div className="row-based centered container">
                    <AgTechIconBadge
                        icon={'fas fa-check'}
                        styles={{
                            backgroundColor: AgTechColors.DarkGreen,
                            color: AgTechColors.White,
                            height: 36,
                            width: 36,
                            borderRadius: 44,
                            fontSize: 16,
                            paddingTop: 2
                        }}
                    />
                    <AgTechText
                        text={props.actionConfiguration.actionConfirmationMessage ?? 'Submission successful!'}
                        classes='font-size-medium font-semibold mt-4'
                    />
                </div>
            </AgTechFadeInView>
        </div>
    )
}

export const showActionModal = async <TInput, TOutput, TContextData>(
    props: AgTechActionModalProps<TInput, TOutput, TContextData>,
    context: {
        showModal: (modalRenderProp: (modalId: string) => React.ReactNode) => void,
        closeModal: (modalId: string) => void
    }
) => {
    logDev('Showing action modal', {
        action: props.action,
        executionProps: props
    });

    context.showModal((modalId) => {
        return (
            <AgTechActionModal
                action={props.action}
                actionProps={props.actionProps}
                closeModal={() => context.closeModal(modalId)}
            />
        )
    });
}